// Shared by the Publish modal, the plan's post cards and the Publishing screen:
// platform names, safe shapes for server data, and media preparation.
import { addDays, parseLocalDate } from '../product/productUtils';

// Plain Material Symbols glyphs, not brand logos (same as Social accounts). Ids match the backend.
export const PUBLISH_PLATFORMS = {
    instagram: { name: 'Instagram', icon: 'photo_camera' },
    facebook: { name: 'Facebook', icon: 'groups' },
    threads: { name: 'Threads', icon: 'alternate_email' },
    linkedin: { name: 'LinkedIn', icon: 'work' },
    pinterest: { name: 'Pinterest', icon: 'push_pin' },
    tiktok: { name: 'TikTok', icon: 'music_note' },
    youtube: { name: 'YouTube', icon: 'smart_display' },
    x: { name: 'X', icon: 'tag' },
};

export const platformLabel = (id) => PUBLISH_PLATFORMS[id]?.name || (id ? id.charAt(0).toUpperCase() + id.slice(1) : 'Unknown');
export const platformIcon = (id) => PUBLISH_PLATFORMS[id]?.icon || 'public';

// "Instagram Reels", "YouTube Shorts", "Twitter" → the platform id they post to
const PLATFORM_ALIASES = {
    instagram: ['instagram', 'ig', 'instagramreels'],
    facebook: ['facebook', 'fb', 'facebookpage'],
    threads: ['threads'],
    tiktok: ['tiktok'],
    youtube: ['youtube', 'youtubeshorts', 'yt'],
    linkedin: ['linkedin'],
    x: ['x', 'twitter', 'xtwitter', 'twitterx'],
    pinterest: ['pinterest'],
};
export const platformIdFor = (name) => {
    const key = String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return Object.keys(PLATFORM_ALIASES).find(id => PLATFORM_ALIASES[id].includes(key)) || '';
};

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const text = (value) => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '');
const count = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? Math.round(number) : 0;
};

// { [platform]: { phase, needsMedia, maxImages, video, textLimit, links, needsBoard } }
export const normalizeCapabilities = (raw) => {
    const platforms = isObject(raw?.platforms) ? raw.platforms : {};
    return Object.keys(platforms).reduce((acc, id) => {
        const item = isObject(platforms[id]) ? platforms[id] : {};
        return {
            ...acc,
            [id]: {
                phase: Number(item.phase) || 1,
                needsMedia: item.needsMedia === true,
                maxImages: count(item.maxImages),
                video: item.video === true,
                textLimit: count(item.textLimit),
                links: item.links !== false,
                needsBoard: item.needsBoard === true,
            },
        };
    }, {});
};

export const JOB_STATUSES = ['queued', 'running', 'processing', 'published', 'failed', 'cancelled'];
export const ACTIVE_STATUSES = ['queued', 'running', 'processing'];

export const normalizeJob = (raw) => ({
    id: text(raw.id),
    platform: text(raw.platform),
    accountId: text(raw.accountId),
    accountHandle: text(raw.accountHandle).replace(/^@+/, ''),
    planWeek: text(raw.planWeek),
    planPostId: text(raw.planPostId),
    text: text(raw.text),
    media: (Array.isArray(raw.media) ? raw.media : []).filter(isObject).map(item => ({
        id: text(item.id),
        url: text(item.url),
        mimeType: text(item.mimeType),
        kind: item.kind === 'video' || /^video\//.test(text(item.mimeType)) ? 'video' : 'image',
    })),
    scheduledAt: text(raw.scheduledAt),
    status: JOB_STATUSES.includes(raw.status) ? raw.status : 'queued',
    attempts: count(raw.attempts),
    lastError: text(raw.lastError),
    externalUrl: /^https?:\/\//i.test(text(raw.externalUrl)) ? text(raw.externalUrl) : '',
    publishedAt: text(raw.publishedAt),
    createdAt: text(raw.createdAt),
    updatedAt: text(raw.updatedAt),
});

export const normalizeJobs = (list) => (Array.isArray(list) ? list : []).filter(isObject).map(normalizeJob).filter(job => job.id);

const POLL_AHEAD_MS = 2 * 60 * 1000;

// Poll while something is publishing, or a scheduled job is due within a couple of minutes.
// (Jobs scheduled days ahead don't need checking every 20 seconds.)
export const needsPolling = (jobs, now = Date.now()) => jobs.some(job => (
    job.status === 'running' || job.status === 'processing'
    || (job.status === 'queued' && (!job.scheduledAt || new Date(job.scheduledAt).getTime() <= now + POLL_AHEAD_MS))
));

const toDate = (value) => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? date : null;
};

// "Thu 6:00 PM" this week, "Oct 9, 6:00 PM" further out
export const formatWhen = (value, now = new Date()) => {
    const date = toDate(value);
    if (!date) return '';
    const days = Math.abs(date.getTime() - now.getTime()) / 86400000;
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (days < 6) return `${date.toLocaleDateString('en-US', { weekday: 'short' })} ${time}`;
    return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(date.getFullYear() !== now.getFullYear() && { year: 'numeric' }) })}, ${time}`;
};

// Errors from the server can be long; one line on a card
export const shortReason = (value, max = 90) => {
    const line = text(value).replace(/\s+/g, ' ').trim() || 'Unknown error';
    return line.length > max ? `${line.slice(0, max - 1)}…` : line;
};

/* ---------- When ---------- */

const WEEK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Minutes after midnight for "6:00 PM", "18:00" or "9am" (as the plan reads them); null when unreadable
export const parseTimeMinutes = (time) => {
    const match = /(\d{1,2})(?::(\d{2}))?\s*([ap])?\.?m?\b/i.exec(text(time));
    if (!match) return null;
    const hours = Number(match[1]) % 12 + (match[3] ? (match[3].toLowerCase() === 'p' ? 12 : 0) : Number(match[1]) >= 12 ? 12 : 0);
    const minutes = hours * 60 + Number(match[2] || 0);
    return minutes < 24 * 60 ? minutes : null;
};

const pad = (n) => String(n).padStart(2, '0');

// A Date as the value of <input type="datetime-local">, in local time
export const toLocalInput = (date) => (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
);

// The post's planned day and time in the plan's week, in local time; null when it has no day
export const plannedDate = (weekStart, day, time) => {
    const index = WEEK_DAYS.indexOf(text(day).toLowerCase());
    if (!weekStart || index < 0) return null;
    const date = parseLocalDate(addDays(weekStart, index));
    const minutes = parseTimeMinutes(time) ?? 9 * 60;
    date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    return date;
};

/* ---------- Media ---------- */

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const VIDEO_TYPES = ['video/mp4', 'video/quicktime'];
export const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 300 * 1024 * 1024;
const MAX_EDGE = 2048;

export const formatBytes = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

// Instagram only takes JPEG, so every image goes up as one: at most 2048px on the long edge,
// same aspect ratio, quality .9. Transparent areas become white rather than black.
export const toJpeg = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
        try {
            const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
            const width = Math.max(1, Math.round(img.naturalWidth * scale));
            const height = Math.max(1, Math.round(img.naturalHeight * scale));
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext('2d');
            context.fillStyle = '#fff';
            context.fillRect(0, 0, width, height);
            context.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
                URL.revokeObjectURL(url);
                if (blob) resolve({ blob, width, height });
                else reject(new Error('Could not convert the image'));
            }, 'image/jpeg', 0.9);
        } catch (e) {
            URL.revokeObjectURL(url);
            reject(e);
        }
    };
    img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Could not read the image'));
    };
    img.src = url;
});

// "photo.png" → "photo.jpg"
export const jpegName = (name) => `${text(name).replace(/\.[^.]+$/, '') || 'image'}.jpg`;
